import { useCallback, useEffect, useMemo, useState } from 'react'

import { ApiError } from '../../lib/api'
import {
  biz, type Contact, type LineInput, type Product, type SalesDoc, type SalesKind,
} from '../../lib/business'
import { money } from '../../lib/format'
import { useToast } from '../../app/Toasts'
import { useAuth } from '../../app/AuthContext'
import { ADD_NEW, QuickAddCustomer, QuickAddProduct } from './QuickAdd'

const TITLES: Record<SalesKind, { title: string; singular: string }> = {
  'quotations': { title: 'Quotations', singular: 'quotation' },
  'sales-orders': { title: 'Sales orders', singular: 'sales order' },
  'invoices': { title: 'Invoices', singular: 'invoice' },
}

const STATUS_TONE: Record<string, string> = {
  draft: 'pill-mute', sent: 'pill-accent', accepted: 'pill-ok',
  confirmed: 'pill-ok', invoiced: 'pill-ok', paid: 'pill-ok',
  partial: 'pill-warn', expired: 'pill-warn', cancelled: 'pill-mute',
}

export default function SalesPage({ kind }: { kind: SalesKind }) {
  const { push } = useToast()
  const [docs, setDocs] = useState<SalesDoc[]>([])
  const [nextNumber, setNextNumber] = useState('')
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState<SalesDoc | null>(null)
  const [composing, setComposing] = useState(false)

  const load = useCallback(async () => {
    try {
      const r = await biz.salesDocs(kind)
      setDocs(r.items)
      setNextNumber(r.next_number)
    } catch { /* ignore */ } finally { setLoading(false) }
  }, [kind])

  useEffect(() => { setLoading(true); setOpen(null); setComposing(false); load() }, [load])

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn()
      push('success', ok)
      load()
      setOpen(null)
    } catch (e) {
      push('error', 'That did not work', e instanceof ApiError ? e.message : undefined)
    }
  }

  const meta = TITLES[kind]

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card">
        <div className="card-head">
          <span className="card-title">{meta.title}</span>
          <div className="grow" />
          <span className="muted mono" style={{ fontSize: 11.5 }}>next: {nextNumber}</span>
          <button className="btn btn-sm btn-primary" onClick={() => setComposing(true)}>
            New {meta.singular}
          </button>
        </div>

        {loading ? (
          <div className="card-body"><div className="skeleton" style={{ height: 120 }} /></div>
        ) : docs.length === 0 ? (
          <div className="empty">
            <div className="empty-title">No {meta.title.toLowerCase()} yet</div>
            <div>Create one, or convert from an earlier document.</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Number</th><th>Customer</th><th className="col-optional">Date</th><th className="col-optional">Tax</th>
                  <th className="num">Total</th>
                  {kind === 'invoices' && <th className="num">Balance</th>}
                  <th>Status</th><th />
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} className="clickable"
                      onClick={() => biz.salesDoc(d.id).then(setOpen).catch(() => {})}>
                    <td className="mono" style={{ fontSize: 12 }}>{d.number}</td>
                    <td>{d.contact?.name ?? '—'}</td>
                    <td className="col-optional">{d.issue_date}</td>
                    <td className="col-optional">
                      <span className={`pill ${d.is_interstate ? 'pill-accent' : 'pill-mute'}`}>
                        {d.is_interstate ? 'IGST' : 'CGST+SGST'}
                      </span>
                    </td>
                    <td className="num" style={{ fontWeight: 560 }}>{money(d.total)}</td>
                    {kind === 'invoices' && (
                      <td className="num" style={{ color: d.balance_due > 0 ? 'var(--warn)' : 'var(--ok)' }}>
                        {money(d.balance_due)}
                      </td>
                    )}
                    <td>
                      <span className={`pill ${STATUS_TONE[d.status] ?? 'pill-mute'}`}>
                        {d.status}
                      </span>
                    </td>
                    <td className="num">
                      <button className="btn btn-sm"
                        onClick={(e) => { e.stopPropagation(); biz.salesDoc(d.id).then(setOpen) }}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {composing && (
        <Composer kind={kind} onClose={() => setComposing(false)}
          onCreated={() => { setComposing(false); load() }} />
      )}

      {open && <DocDetail doc={open} onClose={() => setOpen(null)} act={act} />}
    </div>
  )
}

// --------------------------------------------------------------- detail ---

function DocDetail({ doc, onClose, act }: {
  doc: SalesDoc
  onClose: () => void
  act: (fn: () => Promise<unknown>, ok: string) => Promise<void>
}) {
  const actions: Array<{ label: string; run: () => Promise<unknown>; ok: string; primary?: boolean }> = []
  if (doc.doc_type === 'quotation') {
    if (doc.status === 'draft') actions.push({ label: 'Mark sent', run: () => biz.setStatus(doc.id, 'sent'), ok: 'Marked as sent' })
    if (doc.status === 'sent') actions.push({ label: 'Mark accepted', run: () => biz.setStatus(doc.id, 'accepted'), ok: 'Marked as accepted' })
    if (['sent', 'accepted'].includes(doc.status))
      actions.push({ label: 'Convert to sales order', run: () => biz.convert(doc.id, 'sales_order'), ok: 'Sales order created', primary: true })
  }
  if (doc.doc_type === 'sales_order') {
    if (doc.status === 'draft') actions.push({ label: 'Confirm', run: () => biz.setStatus(doc.id, 'confirmed'), ok: 'Order confirmed' })
    if (doc.status === 'confirmed')
      actions.push({ label: 'Convert to invoice', run: () => biz.convert(doc.id, 'invoice'), ok: 'Invoice created', primary: true })
  }
  if (doc.doc_type === 'invoice' && doc.status === 'draft')
    actions.push({ label: 'Mark sent', run: () => biz.setStatus(doc.id, 'sent'), ok: 'Invoice sent', primary: true })

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">{doc.number}</span>
        <span className={`pill ${STATUS_TONE[doc.status] ?? 'pill-mute'}`}>{doc.status}</span>
        <div className="grow" />
        {actions.map((a) => (
          <button key={a.label} className={`btn btn-sm${a.primary ? ' btn-primary' : ''}`}
            onClick={() => act(a.run, a.ok)}>{a.label}</button>
        ))}
        <button className="btn btn-sm btn-ghost" onClick={onClose}>Close</button>
      </div>

      <div className="card-body">
        <div className="wrap-gap" style={{ fontSize: 12.5, marginBottom: 12 }}>
          <span><span className="muted">Customer</span> <strong>{doc.contact?.name}</strong></span>
          {doc.contact?.gstin && <span className="mono">{doc.contact.gstin}</span>}
          <span><span className="muted">Date</span> {doc.issue_date}</span>
          {doc.due_date && <span><span className="muted">Due</span> {doc.due_date}</span>}
          {doc.valid_until && <span><span className="muted">Valid until</span> {doc.valid_until}</span>}
          <span><span className="muted">Place of supply</span> {doc.place_of_supply_name ?? '—'}</span>
          <span className={`pill ${doc.is_interstate ? 'pill-accent' : 'pill-mute'}`}>
            {doc.is_interstate ? 'Inter-state · IGST' : 'Intra-state · CGST + SGST'}
          </span>
        </div>

        <div className="items-scroll">
          <table className="items">
            <thead>
              <tr>
                <th>#</th><th>Description</th><th>HSN</th><th>Qty</th><th>Unit</th>
                <th>Rate</th><th>Taxable</th><th>GST%</th>
                {doc.is_interstate ? <th>IGST</th> : <><th>CGST</th><th>SGST</th></>}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {doc.lines.map((l) => (
                <tr key={l.line_no}>
                  <td className="row-no">{l.line_no}</td>
                  <td style={{ padding: '5px 6px' }}>{l.description}</td>
                  <td className="mono" style={{ padding: '5px 6px', fontSize: 11.5 }}>{l.hsn_sac ?? '—'}</td>
                  <td style={{ padding: '5px 6px', textAlign: 'right' }}>{l.quantity}</td>
                  <td style={{ padding: '5px 6px' }}>{l.unit}</td>
                  <td style={{ padding: '5px 6px', textAlign: 'right' }}>{money(l.rate)}</td>
                  <td style={{ padding: '5px 6px', textAlign: 'right' }}>{money(l.taxable_value)}</td>
                  <td style={{ padding: '5px 6px', textAlign: 'right' }}>{l.gst_rate}%</td>
                  {doc.is_interstate ? (
                    <td style={{ padding: '5px 6px', textAlign: 'right' }}>{money(l.igst)}</td>
                  ) : (
                    <>
                      <td style={{ padding: '5px 6px', textAlign: 'right' }}>{money(l.cgst)}</td>
                      <td style={{ padding: '5px 6px', textAlign: 'right' }}>{money(l.sgst)}</td>
                    </>
                  )}
                  <td style={{ padding: '5px 6px', textAlign: 'right', fontWeight: 560 }}>
                    {money(l.line_total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <div style={{ minWidth: 280, display: 'grid', gap: 4, fontSize: 13 }}>
            <Row label="Taxable value" value={money(doc.subtotal)} />
            {doc.cgst > 0 && <Row label="CGST" value={money(doc.cgst)} />}
            {doc.sgst > 0 && <Row label="SGST" value={money(doc.sgst)} />}
            {doc.igst > 0 && <Row label="IGST" value={money(doc.igst)} />}
            {doc.round_off !== 0 && <Row label="Round off" value={money(doc.round_off)} />}
            <div style={{ borderTop: '1px solid var(--line)', marginTop: 4, paddingTop: 6 }}>
              <Row label="Total" value={money(doc.total)} strong />
            </div>
            {doc.doc_type === 'invoice' && (
              <>
                <Row label="Paid" value={money(doc.amount_paid)} />
                <Row label="Balance due" value={money(doc.balance_due)} strong />
              </>
            )}
          </div>
        </div>

        <div className="muted" style={{ fontSize: 12, marginTop: 10, fontStyle: 'italic' }}>
          {doc.amount_in_words}
        </div>

        {doc.timeline && doc.timeline.length > 0 && (
          <div style={{ marginTop: 16, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
            <div className="card-title" style={{ marginBottom: 6 }}>History</div>
            {doc.timeline.slice(0, 8).map((t) => (
              <div key={t.id} style={{ fontSize: 12, color: 'var(--text-2)', padding: '2px 0' }}>
                <span className="muted mono" style={{ fontSize: 11 }}>
                  {t.created_at?.slice(0, 16).replace('T', ' ')}
                </span>{'  '}{t.body}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span className="muted">{label}</span>
      <span className="num" style={{ fontWeight: strong ? 650 : 400 }}>{value}</span>
    </div>
  )
}

// -------------------------------------------------------------- composer ---

function Composer({ kind, onClose, onCreated }: {
  kind: SalesKind; onClose: () => void; onCreated: () => void
}) {
  const { push } = useToast()
  // Creating master data needs a higher role than raising a document does.
  // Offering "+ New customer…" to someone the API will refuse is worse than
  // not offering it, so the option is hidden rather than left to fail.
  const { can } = useAuth()
  const canAddMaster = can('vendor:write')
  const [contacts, setContacts] = useState<Contact[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [contactId, setContactId] = useState('')
  const [lines, setLines] = useState<LineInput[]>([{ quantity: 1 }])
  const [busy, setBusy] = useState(false)
  // Which inline creator, if any, is open. Products remember their line so the
  // new product lands on the row the user was filling in.
  const [addingCustomer, setAddingCustomer] = useState(false)
  const [addingProductAt, setAddingProductAt] = useState<number | null>(null)

  useEffect(() => {
    biz.contacts({ role: 'customer' }).then((r) => setContacts(r.items)).catch(() => {})
    biz.products().then((r) => setProducts(r.items)).catch(() => {})
  }, [])

  const setLine = (i: number, patch: Partial<LineInput>) =>
    setLines((cur) => cur.map((l, idx) => (idx === i ? { ...l, ...patch } : l)))

  // Mirrors the server's arithmetic so the total is not a mystery until save.
  // The server recomputes on write; this is a preview, never the source.
  const preview = useMemo(() => {
    let taxable = 0, tax = 0
    for (const l of lines) {
      const p = products.find((x) => x.id === l.product_id)
      const rate = l.rate ?? p?.sale_price ?? 0
      const gstRate = l.gst_rate ?? p?.gst_rate ?? 0
      const gross = (l.quantity || 0) * rate
      const net = gross - gross * ((l.discount_pct || 0) / 100)
      taxable += net
      tax += net * (gstRate / 100)
    }
    return { taxable, tax, total: Math.round(taxable + tax) }
  }, [lines, products])

  // A line needs either a product or a typed description before it means
  // anything. Blocking here explains the problem next to the button instead of
  // bouncing off the server with "every line needs a description".
  const usableLines = lines.filter(
    (l) => l.product_id || (l.description ?? '').trim(),
  )
  const blocker = !contactId
    ? 'Choose a customer'
    : usableLines.length === 0
      ? 'Add at least one line — pick a product, or type a description'
      : null

  const create = async () => {
    setBusy(true)
    try {
      await biz.createSalesDoc(kind, { contact_id: contactId, lines: usableLines })
      push('success', `${TITLES[kind].singular} created`)
      onCreated()
    } catch (e) {
      push('error', 'Could not create', e instanceof ApiError ? e.message : undefined)
    } finally { setBusy(false) }
  }

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">New {TITLES[kind].singular}</span>
        <div className="grow" />
        <button className="btn btn-sm btn-ghost" onClick={onClose}>Cancel</button>
      </div>
      <div className="card-body">
        <div className="form-row" style={{ maxWidth: 380, marginBottom: 18 }}>
          <label>Customer</label>
          <select className="input" value={contactId}
            onChange={(e) => {
              if (e.target.value === ADD_NEW) { setAddingCustomer(true); return }
              setContactId(e.target.value)
            }}>
            <option value="">Select a customer…</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.state_name ? ` · ${c.state_name}` : ''}
              </option>
            ))}
            {canAddMaster && <option value={ADD_NEW}>+ New customer…</option>}
          </select>
        </div>

        {addingCustomer && (
          <QuickAddCustomer
            onCancel={() => setAddingCustomer(false)}
            onDone={(c) => {
              setContacts((cur) => [...cur, c])
              setContactId(c.id)
              setAddingCustomer(false)
            }}
          />
        )}

        <div className="items-scroll" style={{ marginTop: 8 }}>
          <table className="items">
            <thead>
              <tr>
                <th style={{ width: 30 }} /><th style={{ width: 240 }}>Product</th>
                <th style={{ width: 200 }}>Description</th><th style={{ width: 70 }}>Qty</th>
                <th style={{ width: 90 }}>Rate</th><th style={{ width: 70 }}>Disc %</th>
                <th style={{ width: 70 }}>GST %</th><th style={{ width: 30 }} />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const p = products.find((x) => x.id === l.product_id)
                return (
                  <tr key={i}>
                    <td className="row-no">{i + 1}</td>
                    <td>
                      <select className="input" style={{ fontSize: 12.5 }}
                        value={l.product_id ?? ''}
                        onChange={(e) => {
                          if (e.target.value === ADD_NEW) { setAddingProductAt(i); return }
                          setLine(i, { product_id: e.target.value || null })
                        }}>
                        <option value="">Choose a product…</option>
                        {products.map((x) => (
                          <option key={x.id} value={x.id}>{x.name}</option>
                        ))}
                        {canAddMaster && <option value={ADD_NEW}>+ New product…</option>}
                      </select>
                    </td>
                    <td>
                      <input value={l.description ?? ''} placeholder={p?.name ?? 'Description'}
                        onChange={(e) => setLine(i, { description: e.target.value })} />
                    </td>
                    <td>
                      <input className="n" type="number" value={l.quantity}
                        onChange={(e) => setLine(i, { quantity: Number(e.target.value) })} />
                    </td>
                    <td>
                      <input className="n" type="number" step="0.01"
                        value={l.rate ?? p?.sale_price ?? ''}
                        placeholder={String(p?.sale_price ?? '')}
                        onChange={(e) => setLine(i, { rate: Number(e.target.value) })} />
                    </td>
                    <td>
                      <input className="n" type="number" value={l.discount_pct ?? 0}
                        onChange={(e) => setLine(i, { discount_pct: Number(e.target.value) })} />
                    </td>
                    <td>
                      <input className="n" type="number"
                        value={l.gst_rate ?? p?.gst_rate ?? ''}
                        placeholder={String(p?.gst_rate ?? '')}
                        onChange={(e) => setLine(i, { gst_rate: Number(e.target.value) })} />
                    </td>
                    <td>
                      {lines.length > 1 && (
                        <button className="btn btn-ghost btn-sm"
                          onClick={() => setLines((c) => c.filter((_, idx) => idx !== i))}>×</button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {addingProductAt !== null && (
          <QuickAddProduct
            onCancel={() => setAddingProductAt(null)}
            onDone={(p) => {
              setProducts((cur) => [...cur, p])
              setLine(addingProductAt, { product_id: p.id })
              setAddingProductAt(null)
            }}
          />
        )}

        <div className="row" style={{ marginTop: 10 }}>
          <button className="btn btn-sm"
            onClick={() => setLines((c) => [...c, { quantity: 1 }])}>+ Add line</button>
          <div className="grow" />
          <span className="muted" style={{ fontSize: 12.5 }}>
            taxable {money(preview.taxable)} · tax {money(preview.tax)} ·{' '}
            <strong style={{ color: 'var(--text)' }}>approx {money(preview.total)}</strong>
          </span>
        </div>

        <div className="row" style={{ marginTop: 14 }}>
          <button className="btn btn-primary" onClick={create}
            disabled={busy || blocker !== null}>
            {busy && <span className="spinner" />}Create {TITLES[kind].singular}
          </button>
          <span className="muted" style={{ fontSize: 11.5 }}>
            {blocker ?? "Tax heads follow the customer's state; the server recomputes on save."}
          </span>
        </div>
      </div>
    </div>
  )
}
